import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  IconButton
} from '@mui/material';
import { 
  Save as SaveIcon,
  Google as GoogleIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { apiService } from '../services/apiService';

function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    companyName: 'Your Company Name',
    companyVAT: 'AE123456789',
    country: 'AE',
    currency: 'AED',
    dateFormat: 'DD/MM/YYYY',
    timezone: 'Asia/Dubai',
    googleSheetsEnabled: false,
    sheetsId: '',
    autoBackup: true,
    backupFrequency: 'daily',
    emailNotifications: true
  });
  const navigate = useNavigate();
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const canUpdate = user?.permissions?.settings?.includes('update');

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = () => {
    setSaving(true);
    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      alert('Settings saved successfully!');
    }, 1000);
  };

	const handleTestConnection = async () => {
	  setTestResult(null);
	  try {
		const result = await apiService.testSheetsConnection();
		setTestResult({
		  success: result.success,
		  message: result.message
		});
	  } catch (error) {
		setTestResult({
		  success: false,
		  message: error.message || 'Connection test failed'
		});
	  }
	};
  const handleExportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'vat-accounting-settings.json';
    link.click();
  };

  return (
    <Box>
		{/* Header */}
		<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
		  <Box display="flex" alignItems="center" gap={2}>
			<IconButton 
			  onClick={() => navigate('/dashboard')}
			  color="primary"
			>
			  <ArrowBackIcon />
			</IconButton>
			<Typography variant="h4">Settings</Typography>
		  </Box>
		  <Button
			variant="contained"
			startIcon={<SaveIcon />}
			onClick={handleSaveSettings}
			disabled={!canUpdate || saving}
		  >
			{saving ? 'Saving...' : 'Save Settings'}
		  </Button>
		</Box>

      <Grid container spacing={3}>
        {/* Company Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Company Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={settings.companyName}
                    onChange={(e) => handleSettingChange('companyName', e.target.value)}
                    disabled={!canUpdate}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="VAT Registration Number"
                    value={settings.companyVAT}
                    onChange={(e) => handleSettingChange('companyVAT', e.target.value)}
                    disabled={!canUpdate}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!canUpdate}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={settings.country}
                      onChange={(e) => handleSettingChange('country', e.target.value)}
                      label="Country"
                    >
                      <MenuItem value="AE">United Arab Emirates</MenuItem>
                      <MenuItem value="SA">Saudi Arabia</MenuItem>
                      <MenuItem value="BH">Bahrain</MenuItem>
                      <MenuItem value="QA">Qatar</MenuItem>
                      <MenuItem value="OM">Oman</MenuItem>
                      <MenuItem value="KW">Kuwait</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!canUpdate}>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={settings.currency}
                      onChange={(e) => handleSettingChange('currency', e.target.value)}
                      label="Currency"
                    >
                      <MenuItem value="AED">AED - UAE Dirham</MenuItem>
                      <MenuItem value="SAR">SAR - Saudi Riyal</MenuItem>
                      <MenuItem value="BHD">BHD - Bahraini Dinar</MenuItem>
                      <MenuItem value="QAR">QAR - Qatari Riyal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Google Sheets Integration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <GoogleIcon style={{ marginRight: '8px', color: '#4285f4' }} />
                <Typography variant="h6">
                  Google Sheets Integration
                </Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.googleSheetsEnabled}
                    onChange={(e) => handleSettingChange('googleSheetsEnabled', e.target.checked)}
                    disabled={!canUpdate}
                  />
                }
                label="Enable Google Sheets Integration"
              />

              {settings.googleSheetsEnabled && (
                <Box mt={2}>
                  <TextField
                    fullWidth
                    label="Google Sheets ID"
                    value={settings.sheetsId}
                    onChange={(e) => handleSettingChange('sheetsId', e.target.value)}
                    placeholder="Enter your Google Sheets ID"
                    disabled={!canUpdate}
                    helperText="Found in your Google Sheets URL: https://docs.google.com/spreadsheets/d/[SHEET_ID]/edit"
                  />
                  
                  <Box mt={2} display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleTestConnection}
                      disabled={!canUpdate}
                    >
                      Test Connection
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => alert('Template download will be implemented')}
                      disabled={!canUpdate}
                    >
                      Download Template
                    </Button>
                  </Box>

                  {testResult && (
                    <Alert 
                      severity={testResult.success ? 'success' : 'error'} 
                      style={{ marginTop: '10px' }}
                    >
                      {testResult.message}
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Application Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Application Settings
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!canUpdate}>
                    <InputLabel>Date Format</InputLabel>
                    <Select
                      value={settings.dateFormat}
                      onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                      label="Date Format"
                    >
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!canUpdate}>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={settings.timezone}
                      onChange={(e) => handleSettingChange('timezone', e.target.value)}
                      label="Timezone"
                    >
                      <MenuItem value="Asia/Dubai">Dubai (UTC+4)</MenuItem>
                      <MenuItem value="Asia/Riyadh">Riyadh (UTC+3)</MenuItem>
                      <MenuItem value="Asia/Bahrain">Bahrain (UTC+3)</MenuItem>
                      <MenuItem value="Asia/Qatar">Qatar (UTC+3)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider style={{ margin: '20px 0' }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoBackup}
                    onChange={(e) => handleSettingChange('autoBackup', e.target.checked)}
                    disabled={!canUpdate}
                  />
                }
                label="Automatic Backup"
              />

              {settings.autoBackup && (
                <FormControl fullWidth style={{ marginTop: '10px' }} disabled={!canUpdate}>
                  <InputLabel>Backup Frequency</InputLabel>
                  <Select
                    value={settings.backupFrequency}
                    onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                    label="Backup Frequency"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    disabled={!canUpdate}
                  />
                }
                label="Email Notifications"
                style={{ marginTop: '10px' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Data Management */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <StorageIcon style={{ marginRight: '8px' }} />
                <Typography variant="h6">
                  Data Management
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleExportSettings}
                  disabled={!canUpdate}
                >
                  Export Settings
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => alert('Data export will be implemented')}
                  disabled={!canUpdate}
                >
                  Export All Data
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
                      alert('Data clearing will be implemented');
                    }
                  }}
                  disabled={!canUpdate}
                >
                  Clear All Data
                </Button>
              </Box>

              <Typography variant="body2" color="textSecondary" style={{ marginTop: '20px' }}>
                Last backup: {settings.autoBackup ? '2024-01-20 02:00' : 'Never'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Settings;